import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/crm/Badges";
import { Button } from "@/components/ui/button";
import { Calendar, CheckCircle2, Loader2, AlertCircle, Link2, Unlink } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function Account() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [gcal, setGcal] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/google/status").then((r) => setGcal(r.data)).catch(() => setGcal({ configured: false, connected: false }));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const s = params.get("gcal");
    if (s === "connected") { toast.success("Google Calendar collegato"); }
    if (s === "error") { toast.error("Collegamento Google Calendar non riuscito"); }
  }, [params]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/google/auth-url");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Integrazione Google non configurata");
      setBusy(false);
    }
  };
  const disconnect = async () => {
    setBusy(true);
    await api.post("/google/disconnect");
    setBusy(false);
    toast.success("Google Calendar disconnesso");
    load();
  };

  return (
    <div className="space-y-6 animate-fade-up max-w-2xl">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-black tracking-tight">Impostazioni personali</h1>
          <p className="text-sm text-muted-foreground">Il tuo account e le integrazioni personali.</p>
        </div>
        {gcal && gcal.configured && (
          gcal.connected ? (
            <span data-testid="gcal-indicator-connected" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="w-3.5 h-3.5" /> Google Calendar collegato
            </span>
          ) : (
            <span data-testid="gcal-indicator-disconnected" className="inline-flex items-center gap-1.5 rounded-full border border-muted-foreground/25 bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
              <Unlink className="w-3.5 h-3.5" /> Google Calendar non collegato
            </span>
          )
        )}
      </div>

      <div className="rounded-lg border bg-card p-5 flex items-center gap-4">
        <UserAvatar user={user} size={48} />
        <div>
          <div className="font-heading font-bold text-lg">{user?.name}</div>
          <div className="text-sm text-muted-foreground">{user?.email} · <span className="capitalize">{user?.role}</span></div>
        </div>
      </div>

      <div className="rounded-lg border bg-card p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md bg-blue-500/10 text-blue-500 grid place-items-center"><Calendar className="w-5 h-5" /></div>
          <div>
            <h2 className="font-heading font-bold">Google Calendar</h2>
            <p className="text-xs text-muted-foreground">Sincronizza i tuoi appuntamenti CRM sul tuo Google Calendar personale.</p>
          </div>
        </div>

        {!gcal ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : !gcal.configured ? (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm flex items-start gap-2" data-testid="gcal-not-configured">
            <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
            <div>
              <b>Integrazione non ancora configurata.</b> L'amministratore deve fornire le credenziali Google OAuth (Client ID, Client Secret, Redirect URI) per abilitare il collegamento. Il CRM e il calendario interno funzionano normalmente anche senza questa integrazione.
            </div>
          </div>
        ) : gcal.connected ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400" data-testid="gcal-connected">
              <CheckCircle2 className="w-4 h-4" /> Google Calendar connesso{gcal.email ? ` · ${gcal.email}` : ""}
            </div>
            <Button variant="outline" onClick={disconnect} disabled={busy} className="gap-1" data-testid="gcal-disconnect">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />} Disconnetti Google Calendar
            </Button>
          </div>
        ) : (
          <Button onClick={connect} disabled={busy} className="gap-1" data-testid="gcal-connect">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Collega Google Calendar
          </Button>
        )}
      </div>
    </div>
  );
}
